import type {
  IAccountBalanceResponse,
  ITransactionDetailResponse,
  ITransactionResponse,
  ITransactionTypeResponse,
} from '~/interfaces/dto/wallet/response';
import { useAuthStore } from '~/stores/auth.store';
import type { ITransactionRequest } from '~/interfaces/dto/wallet/request';
import { useBonusService } from '~/composables/services/useBonusService';
import { useAuthService } from '~/composables/services/useAuthService';

export const useWalletService = () => {
  const { $fetch } = useNuxtApp();
  const authStore = useAuthStore();
  const { $enabled } = useNuxtApp();
  // transactions
  const fetchTransactions = async (
    body: ITransactionRequest,
  ): Promise<ITransactionResponse> => {
    try {
      return await $fetch<ITransactionResponse>('v2/Wallet/transactions', {
        base: 'balance',
        method: 'post',
        useAuthentication: true,
        useBrandId: true,
        body,
      });
    } catch (e) {
      console.error(e);
    }
  };
  const fetchTransactionDetail = async (
    transactionId: string,
  ): Promise<ITransactionDetailResponse> => {
    try {
      return $fetch<ITransactionDetailResponse>(
        'v2/Wallet/transaction-details',
        {
          base: 'balance',
          useBrandId: true,
          useAuthentication: true,
          query: { financialReference: transactionId },
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  //TODO: check this, seems not to be used
  const fetchBonusTransactions = async (
    request: ITransactionRequest,
  ): Promise<ITransactionResponse> => {
    try {
      return await $fetch<ITransactionResponse>(
        'v2/Wallet/transactions-bonus',
        {
          method: 'post',
          base: 'balance',
          useAuthentication: true,
          useBrandId: true,
          body: request,
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  const fetchTransactionCategories = async () => {
    try {
      return await $fetch<ITransactionTypeResponse>(
        'v2/Wallet/transaction-type-categories',
        {
          base: 'balance',
          useAuthentication: true,
          useBrandId: true,
        },
      );
    } catch (e) {
      console.error(e);
    }
  };
  // balances
  const fetchBalances = async (): Promise<IAccountBalanceResponse> => {
    authStore.setUserBalances({});
    try {
      if (!authStore.access_token)
        return {
          data: {
            cashBalance: 0,
            bonusBalance: 0,
            ticketBalance: 0,
            playThroughBalance: 0,
            lastBonusCreditTransactionDateTime: new Date(),
          },
          isSuccessful: false,
          responseCode: 401,
          error: 'Access token null',
          metadata: {},
          httpCodeEnum: 401,
        };
      return await $fetch<IAccountBalanceResponse>('v2/Wallet/balance', {
        base: 'balance',
        useAuthentication: true,
        useBrandId: true,
        async onResponse({ response }) {
          authStore.setUserBalances(response._data.data);

          const bonusPromises = [];

          if ($enabled('myaccount.enablerewards')) {
            bonusPromises.push(useAuthService().getRewardsSummary());
          }

          bonusPromises.push(useBonusService().getShowBonusBalance());

          await Promise.allSettled([...bonusPromises]);
        },
        onResponseError() {
          authStore.setUserBalances({
            cashBalance: -1,
            bonusBalance: -1,
            playThroughBalance: -1,
          });
        },
      });
    } catch (e) {
      console.error(e);
    }
  };
  return {
    fetchTransactions,
    fetchTransactionDetail,
    fetchBonusTransactions,
    fetchTransactionCategories,
    fetchBalances,
  };
};
